/**
 * React hooks for device-mode / orientation, backed by matchMedia.
 */

import { useSyncExternalStore } from 'react';
import { isTouch } from '../game/deviceMode';

/**
 * Reactive touch-primary flag. Delegates to the single `isTouch()` predicate (see deviceMode.ts)
 * and re-renders when the pointer/hover capabilities change (e.g. a 2-in-1 docking its keyboard),
 * mirroring `useIsPortrait` below.
 */
export function useIsTouch(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const coarse = window.matchMedia('(pointer: coarse)');
      const hover = window.matchMedia('(hover: none)');
      coarse.addEventListener('change', cb);
      hover.addEventListener('change', cb);
      return () => {
        coarse.removeEventListener('change', cb);
        hover.removeEventListener('change', cb);
      };
    },
    () => isTouch(),
    () => false,
  );
}

/** Reactive portrait/landscape flag (re-renders on orientation change). */
export function useIsPortrait(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(orientation: portrait)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(orientation: portrait)').matches,
    () => false,
  );
}

/**
 * Reactive "desktop layout" flag, at the SAME 768 px breakpoint Tailwind's `md:` uses — layout
 * logic in JS (the explore panel's lens shift) and the CSS that positions it must agree, or the
 * world gets re-centred for a card that is somewhere else.
 */
export function useIsDesktopLayout(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(min-width: 768px)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(min-width: 768px)').matches,
    () => true,
  );
}
